/**
 * BagelChat — floating in-app AI chat launcher.
 *
 * Renders a fixed yellow bubble in the bottom-right corner (positioned
 * above the mobile bottom nav). Tap opens a chat panel — full-screen on
 * mobile, ~380x580 popup on desktop — that talks to the askBagel
 * Cloud Function.
 *
 * Only mounts when the user is signed in (the callable requires auth).
 * History is persisted to localStorage across reloads.
 */
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../contexts/AuthContext";
import {
  askBagel,
  clearBagelHistory,
  loadBagelHistory,
  saveBagelHistory,
  type BagelMessage,
} from "../services/bagel";

const SUGGESTED_PROMPTS_ZH = [
  "两个 league 有啥区别？",
  "怎么成为会员？",
  "如何报名比赛？",
  "我的报名费什么时候到？",
];
const SUGGESTED_PROMPTS_EN = [
  "What's the difference between the leagues?",
  "How do I become a member?",
  "How do I sign up for a tournament?",
  "When do I get paid as an organizer?",
];

export default function BagelChat() {
  const { user } = useAuth();
  const { i18n } = useTranslation();
  const zh = i18n?.language?.startsWith("zh") ?? false;

  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<BagelMessage[]>([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  // Load persisted history on first open.
  useEffect(() => {
    if (open && messages.length === 0) {
      setMessages(loadBagelHistory());
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // Persist every change.
  useEffect(() => {
    if (messages.length > 0) saveBagelHistory(messages);
  }, [messages]);

  // Auto-scroll to bottom on new message.
  useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
  }, [messages, sending]);

  if (!user) return null;

  const suggestions = zh ? SUGGESTED_PROMPTS_ZH : SUGGESTED_PROMPTS_EN;

  async function send(rawText?: string) {
    const text = (rawText ?? input).trim();
    if (!text || sending) return;
    setError(null);
    setInput("");
    const userMsg: BagelMessage = { role: "user", content: text, at: Date.now() };
    const next = [...messages, userMsg];
    setMessages(next);
    setSending(true);
    try {
      const { reply } = await askBagel(text, messages);
      setMessages([
        ...next,
        { role: "assistant", content: reply, at: Date.now() },
      ]);
    } catch (e: any) {
      // Firebase callable errors surface as `.message` (HttpsError) or
      // sometimes `.details`.
      const msg =
        e?.message ??
        (zh
          ? "Bagel 出错了，稍后再试一下 🥯"
          : "Something went wrong. Try again in a moment 🥯");
      setError(String(msg));
    } finally {
      setSending(false);
      // Return focus to input so power users can keep typing.
      setTimeout(() => textareaRef.current?.focus(), 20);
    }
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      void send();
    }
  }

  function handleClear() {
    setMessages([]);
    clearBagelHistory();
    setError(null);
  }

  // ---------------- launcher bubble ----------------
  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        aria-label={zh ? "问 Bagel" : "Ask Bagel"}
        className="fixed z-40 flex items-center gap-2 rounded-full bg-optic-yellow text-court-blue-dark shadow-lg hover:shadow-xl transition-shadow font-bold"
        style={{
          right: 16,
          bottom: 80,
          padding: "10px 16px 10px 12px",
        }}
      >
        <span
          className="flex items-center justify-center rounded-full bg-court-blue-dark text-optic-yellow"
          style={{ width: 36, height: 36, fontSize: 20 }}
        >
          🥯
        </span>
        <span className="hidden sm:inline text-sm">
          {zh ? "问 Bagel" : "Ask Bagel"}
        </span>
      </button>
    );
  }

  // ---------------- open panel ----------------
  return (
    <>
      <style>{`
        .bagel-panel {
          position: fixed;
          inset: 0;
          z-index: 50;
          background: white;
          box-shadow: 0 25px 50px -12px rgba(0,0,0,0.35);
          display: flex;
          flex-direction: column;
        }
        @media (min-width: 640px) {
          .bagel-panel {
            inset: auto;
            right: 16px;
            bottom: 80px;
            width: 380px;
            height: 580px;
            max-height: calc(100vh - 96px);
            border-radius: 16px;
            overflow: hidden;
          }
        }
        .bagel-dot {
          display: inline-block;
          width: 6px;
          height: 6px;
          border-radius: 999px;
          background: #6b7280;
          animation: bagelPulse 1s infinite ease-in-out;
        }
        @keyframes bagelPulse {
          0%, 80%, 100% { opacity: 0.3; transform: scale(0.8); }
          40% { opacity: 1; transform: scale(1); }
        }
      `}</style>
      <div className="bagel-panel">
        {/* Header */}
        <div className="flex items-center gap-2 px-4 py-3 bg-court-blue-dark text-optic-yellow">
          <span style={{ fontSize: 22 }}>🥯</span>
          <div className="flex-1 min-w-0">
            <div className="font-bold text-sm leading-tight">Bagel Bagel</div>
            <div className="text-[11px] opacity-80 truncate">
              {zh ? "一问就答 · AI 助手" : "Ask anything · AI assistant"}
            </div>
          </div>
          {messages.length > 0 && (
            <button
              onClick={handleClear}
              className="text-[11px] px-2 py-1 rounded hover:bg-white/10"
              aria-label={zh ? "清空对话" : "Clear history"}
              title={zh ? "清空对话" : "Clear history"}
            >
              {zh ? "清空" : "Clear"}
            </button>
          )}
          <button
            onClick={() => setOpen(false)}
            className="text-lg px-2 py-1 rounded hover:bg-white/10"
            aria-label={zh ? "关闭" : "Close"}
          >
            ×
          </button>
        </div>

        {/* Message list */}
        <div
          ref={listRef}
          className="flex-1 overflow-y-auto px-3 py-3 bg-gray-50"
        >
          {messages.length === 0 && (
            <div className="text-center text-gray-500 text-sm mt-4">
              <div className="text-3xl mb-2">🥯</div>
              <div className="mb-3">
                {zh
                  ? "嗨！我是 Bagel Bagel，问我关于 Acework 的任何问题～"
                  : "Hi! I'm Bagel Bagel. Ask me anything about Acework."}
              </div>
              <div className="flex flex-col gap-2 mt-4">
                {suggestions.map((s) => (
                  <button
                    key={s}
                    onClick={() => void send(s)}
                    className="text-left text-sm px-3 py-2 rounded-lg bg-white border border-gray-200 hover:border-optic-yellow hover:bg-optic-yellow/10 transition-colors"
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}

          {messages.map((m, i) => (
            <div
              key={i}
              className={`mb-2 flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
            >
              <div
                className={`max-w-[80%] px-3 py-2 rounded-2xl text-sm whitespace-pre-wrap break-words ${
                  m.role === "user"
                    ? "bg-court-blue-dark text-white rounded-br-sm"
                    : "bg-white text-gray-900 border border-gray-200 rounded-bl-sm"
                }`}
              >
                {m.content}
              </div>
            </div>
          ))}

          {sending && (
            <div className="flex justify-start mb-2">
              <div className="px-3 py-2 rounded-2xl bg-white border border-gray-200 rounded-bl-sm">
                <div className="flex gap-1 items-center h-4">
                  <span className="bagel-dot" />
                  <span className="bagel-dot" style={{ animationDelay: "0.15s" }} />
                  <span className="bagel-dot" style={{ animationDelay: "0.3s" }} />
                </div>
              </div>
            </div>
          )}

          {error && (
            <div className="mt-2 px-3 py-2 rounded-lg bg-red-50 border border-red-200 text-red-700 text-xs">
              {error}
            </div>
          )}
        </div>

        {/* Input */}
        <div className="border-t border-gray-200 bg-white p-2 flex items-end gap-2">
          <textarea
            ref={textareaRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={
              zh ? "问点什么…（Enter 发送）" : "Ask something… (Enter to send)"
            }
            rows={1}
            maxLength={2000}
            disabled={sending}
            className="flex-1 resize-none border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-court-blue-dark max-h-32"
            style={{ minHeight: 40 }}
          />
          <button
            onClick={() => void send()}
            disabled={sending || !input.trim()}
            className="px-3 py-2 rounded-lg bg-optic-yellow text-court-blue-dark font-bold text-sm disabled:opacity-40 disabled:cursor-not-allowed"
            aria-label={zh ? "发送" : "Send"}
          >
            {zh ? "发送" : "Send"}
          </button>
        </div>

      </div>
    </>
  );
}
